import React, { useState, useEffect, useRef } from 'react';
import { TopBar } from '../components/TopBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';
import { AddTaskModal } from '../components/AddTaskModal.jsx';
import { LoadingScreen } from '../components/LoadingScreen.jsx';
import { WelcomeOnboarding } from '../components/WelcomeOnboarding.jsx';
import { PointBurst } from '../components/PointBurst.jsx';
import { MilestoneAlert, getMilestone } from '../components/MilestoneAlert.jsx';
import { CompletionNotification } from '../components/CompletionNotification.jsx';
import { InvitePartnerModal } from '../components/InvitePartnerModal.jsx';
import { InvitePartnerBanner } from '../components/InvitePartnerBanner.jsx';
import { InstallAppPrompt } from '../components/InstallAppPrompt.jsx';
import { DashboardPage } from './DashboardPage.jsx';
import { TasksPage } from './TasksPage.jsx';
import { CalendarPage } from './CalendarPage.jsx';
import { ShoppingPage } from './ShoppingPage.jsx';
import { StatsPage } from './StatsPage.jsx';
import { SettingsPage } from './SettingsPage.jsx';
import { useAppData } from '../hooks/useAppData.js';
import { DARK_THEME, LIGHT_THEME } from '../lib/theme.js';

export function MainApp({ user }) {
  const [view, setView] = useState('dashboard');
  const [tasksTab, setTasksTab] = useState('all'); // pre-selected tab when navigating to tasks
  const [showAddTask, setShowAddTask] = useState(false);
  const [addTaskMode, setAddTaskMode] = useState('mine');
  const [editingTask, setEditingTask] = useState(null);
  const [pointBurst, setPointBurst] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [completionNotif, setCompletionNotif] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showInvitePartner, setShowInvitePartner] = useState(false);
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return 'dark';
  });
  const [localProfile, setLocalProfile] = useState(null);

  const data = useAppData(user, {
    onPartnerCompletion: (task) => {
      setCompletionNotif({
        taskTitle: task.title,
        points: task.tagPoints,
        message: task.message,
        reward: task.reward,
        rewardEmoji: task.rewardEmoji,
      });
    },
    onInviteAccepted: (notif, newPartner) => {
      // Show a celebratory milestone-style alert
      setMilestone({ custom: true, partnerName: newPartner?.name });
    },
  });
  const theme = themeMode === 'dark' ? DARK_THEME : LIGHT_THEME;
  const profile = localProfile || data.profile;

  // Sync theme with profile when it loads
  useEffect(() => {
    if (data.profile && data.profile.themeMode) {
      setThemeMode(data.profile.themeMode);
    }
  }, [data.profile]);

  // Track previous love score so we can detect milestone crossings
  const prevLoveScore = useRef(null);
  useEffect(() => {
    if (!data.pairStats) return;
    const newScore = data.pairStats.loveScore || 0;
    if (prevLoveScore.current !== null) {
      const m = getMilestone(prevLoveScore.current, newScore);
      if (m) setMilestone(m);
    }
    prevLoveScore.current = newScore;
  }, [data.pairStats]);

  // Show onboarding for first-time users.
  useEffect(() => {
    if (!data.profile) return;
    const seenKey = `tt_onboarding_seen_${data.profile.id}`;
    const seen = localStorage.getItem(seenKey);
    if (!seen) {
      setShowOnboarding(true);
    }
  }, [data.profile]);

  function dismissOnboarding() {
    if (data.profile) {
      localStorage.setItem(`tt_onboarding_seen_${data.profile.id}`, 'true');
    }
    setShowOnboarding(false);
  }

  function openAddTask(mode) {
    setAddTaskMode(mode);
    setShowAddTask(true);
  }

  async function handleSubmitTask(taskData, taskId) {
    if (taskId) {
      await data.actions.updateTask(taskId, taskData);
    } else {
      await data.actions.addTask(taskData);
    }
  }

  async function handleComplete(taskId) {
    const result = await data.actions.completeTask(taskId);
    if (!result) return;

    // For partner-sent tasks: full love-points burst with point counter
    // For own private/shared tasks: simple confetti celebration
    if (result.taskList === 'sent' && result.points > 0) {
      setPointBurst({
        points: result.points,
        kind: result.wasLate ? 'late' : result.wasEarly ? 'early' : 'love',
      });
    } else {
      // Just celebrate — confetti only, no point counter
      setPointBurst({
        points: 0,
        kind: 'celebrate',
      });
    }
  }

  function handleThemeChange(newMode) {
    setThemeMode(newMode);
  }

  function handleProfileUpdate(updated) {
    const camelUpdated = {
      ...updated,
      themeMode: updated.theme_mode,
      notificationsEnabled: updated.notifications_enabled,
      notifyOnNewTask: updated.notify_on_new_task,
      notifyOnTaskCompleted: updated.notify_on_task_completed,
      authProvider: updated.auth_provider,
      photoUrl: updated.photo_url,
    };
    setLocalProfile(camelUpdated);
    if (camelUpdated.themeMode) {
      setThemeMode(camelUpdated.themeMode);
    }
  }

  if (data.loading) {
    return <LoadingScreen theme={theme} />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      color: theme.text,
      paddingBottom: 80,
    }}>
      <TopBar
        theme={theme}
        user={profile}
        partner={data.partner}
        pairStats={data.pairStats}
        onSettingsClick={() => setView('settings')}
      />

      <main style={{ maxWidth: 720, margin: '0 auto', padding: 18 }}>
        {view === 'dashboard' && (
          <>
            <InstallAppPrompt theme={theme} variant="banner" />
            <DashboardPage
              theme={theme}
              profile={profile}
              partner={data.partner}
              pair={data.pair}
              pairStats={data.pairStats}
              tasks={data.tasks}
              completions={data.completions}
              onAddTask={openAddTask}
              onComplete={handleComplete}
              onDelete={data.actions.deleteTask}
              onEdit={setEditingTask}
              onGoToShopping={() => setView('shopping')}
              onGoToTasks={(tab) => { setTasksTab(tab || 'all'); setView('tasks'); }}
              onGoToStats={() => setView('stats')}
              onScrollToCompletedToday={() => {
                setTimeout(() => {
                  const el = document.getElementById('completed-today-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
              }}
            />
          </>
        )}
        {view === 'tasks' && (
          <TasksPage
            theme={theme}
            profile={profile}
            partner={data.partner}
            tasks={data.tasks}
            initialTab={tasksTab}
            onAddTask={openAddTask}
            onComplete={handleComplete}
            onDelete={data.actions.deleteTask}
            onEdit={setEditingTask}
          />
        )}
        {view === 'calendar' && (
          <CalendarPage
            theme={theme}
            profile={profile}
            partner={data.partner}
            tasks={data.tasks}
            onComplete={handleComplete}
            onDelete={data.actions.deleteTask}
            onEdit={setEditingTask}
          />
        )}
        {view === 'shopping' && (
          <ShoppingPage
            theme={theme}
            profile={profile}
            partner={data.partner}
            pair={data.pair}
          />
        )}
        {view === 'stats' && (
          <StatsPage
            theme={theme}
            profile={profile}
            partner={data.partner}
            pair={data.pair}
            pairStats={data.pairStats}
            completions={data.completions}
          />
        )}
        {view === 'settings' && (
          <SettingsPage
            theme={theme}
            profile={profile}
            partner={data.partner}
            pair={data.pair}
            themeMode={themeMode}
            onThemeChange={handleThemeChange}
            onProfileUpdate={handleProfileUpdate}
            onShowOnboarding={() => setShowOnboarding(true)}
            onInvitePartner={() => setShowInvitePartner(true)}
          />
        )}

        {/* Invite partner banner — shown on all pages when user has no partner.
            Hidden on settings since there's already a dedicated invite button there. */}
        {!data.pair && view !== 'settings' && (
          <InvitePartnerBanner theme={theme} onInvite={() => setShowInvitePartner(true)} />
        )}
      </main>

      <BottomNav theme={theme} view={view} onChange={setView} />

      {showAddTask && (
        <AddTaskModal
          theme={theme}
          mode={addTaskMode}
          partner={data.partner}
          onClose={() => setShowAddTask(false)}
          onSubmit={handleSubmitTask}
        />
      )}

      {editingTask && (
        <AddTaskModal
          theme={theme}
          mode="edit"
          partner={data.partner}
          editTask={editingTask}
          onClose={() => setEditingTask(null)}
          onSubmit={handleSubmitTask}
        />
      )}

      {pointBurst && (
        <PointBurst
          theme={theme}
          points={pointBurst.points}
          kind={pointBurst.kind}
          onDone={() => setPointBurst(null)}
        />
      )}

      {milestone && (
        <MilestoneAlert
          theme={theme}
          milestone={milestone}
          onClose={() => setMilestone(null)}
        />
      )}

      {completionNotif && (
        <CompletionNotification
          theme={theme}
          partner={data.partner}
          taskTitle={completionNotif.taskTitle}
          points={completionNotif.points}
          message={completionNotif.message}
          reward={completionNotif.reward}
          rewardEmoji={completionNotif.rewardEmoji}
          onClose={() => setCompletionNotif(null)}
        />
      )}

      {showOnboarding && (
        <WelcomeOnboarding
          theme={theme}
          onClose={dismissOnboarding}
        />
      )}

      {showInvitePartner && (
        <InvitePartnerModal
          theme={theme}
          onClose={() => setShowInvitePartner(false)}
        />
      )}
    </div>
  );
}
